import { X, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { FilterDropdown } from "./common/FilterDropdown";
import {
  Clubhouse_LgAdjudicationIssueTypeV2,
  Clubhouse_LgAdjudicationIssueSeverityV2,
} from "../enums";
import { issueTypeLabel, severityLabel } from "./common/adjudicationColumns";

// Enum values are numeric; FilterDropdown works in strings, so options carry the
// stringified enum value and the page compares against String(issue.field).
const numericValues = <T extends number>(e: Record<string, string | number>) =>
  Object.values(e).filter((v): v is T => typeof v === "number");

const TYPE_OPTIONS = numericValues<Clubhouse_LgAdjudicationIssueTypeV2>(
  Clubhouse_LgAdjudicationIssueTypeV2,
)
  // UNKNOWN is a fallback bucket, not a user-facing filter.
  .filter((v) => v !== Clubhouse_LgAdjudicationIssueTypeV2.UNKNOWN)
  .map((v) => ({ value: String(v), label: issueTypeLabel[v] }));

// Severity is a string enum (values ARE the wire values, e.g. "Blocking"), so
// its options carry the value directly — no numeric stringification.
const SEVERITY_OPTIONS = (
  Object.values(
    Clubhouse_LgAdjudicationIssueSeverityV2,
  ) as Clubhouse_LgAdjudicationIssueSeverityV2[]
).map((v) => ({ value: v, label: severityLabel[v] }));

export interface AdjudicationQueueFiltersProps {
  leagueOptions: { value: string; label: string }[];
  leagues: string[];
  types: string[];
  severities: string[];
  search: string;
  onLeaguesChange: (v: string[]) => void;
  onTypesChange: (v: string[]) => void;
  onSeveritiesChange: (v: string[]) => void;
  onSearchChange: (v: string) => void;
}

export function AdjudicationQueueFilters({
  leagueOptions,
  leagues,
  types,
  severities,
  search,
  onLeaguesChange,
  onTypesChange,
  onSeveritiesChange,
  onSearchChange,
}: AdjudicationQueueFiltersProps) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <FilterDropdown
        label="Leagues"
        options={leagueOptions}
        selected={leagues}
        onChange={onLeaguesChange}
      />
      <FilterDropdown
        label="Issue Types"
        options={TYPE_OPTIONS}
        selected={types}
        onChange={onTypesChange}
      />
      <FilterDropdown
        label="Severities"
        options={SEVERITY_OPTIONS}
        selected={severities}
        onChange={onSeveritiesChange}
      />
      <div className="relative ml-auto">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-text-muted pointer-events-none" />
        <Input
          placeholder="Search issues..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="h-8 w-56 pl-8 text-xs rounded-md bg-surface border-border"
        />
        {search && (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => onSearchChange("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary transition-colors"
          >
            <X className="size-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
